/**
 * Tipografie SoftFactura — preset-uri disponibile pentru utilizatori.
 *
 * 5 fonturi curated (top SaaS standards) + 4 mărimi de bază.
 *
 * Mecanism:
 * - `--font-family`: stack-ul CSS aplicat pe body
 * - `--font-base-size-pct`: procentul aplicat pe `<html>` ca `font-size`,
 *   adică 1rem = 16px * pct. Toate font-size-urile din UI sunt în `rem`,
 *   deci scalarea e uniformă pe toate paginile când utilizatorul schimbă
 *   mărimea în /setari?tab=appearance.
 *
 * Bootstrap inline în layout.tsx aplică ambele înainte de hydration ca
 * să nu existe flash de font default sau de mărime greșită la încărcare.
 */

export type FontFamilyId = 'system' | 'inter' | 'ibm-plex' | 'manrope' | 'roboto' | 'geist' | 'mona-sans' | 'satoshi' | 'dm-sans' | 'switzer' | 'general-sans';
export type FontSizeId = 'sm' | 'md' | 'lg' | 'xl';

export interface FontPreset {
  id: FontFamilyId;
  label: string;
  tagline: string;
  /** Stack CSS complet pentru acest font (cu fallback-uri). */
  fontStack: string;
  /** URL Google Fonts pentru încărcare; null pentru fonturi system. */
  googleFontsHref: string | null;
}

export const FONT_PRESETS: Record<FontFamilyId, FontPreset> = {
  system: {
    id: 'system',
    label: 'Sistem',
    tagline: 'Folosește fontul native al sistemului tău (zero încărcare).',
    fontStack: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, Roboto, "Helvetica Neue", Arial, sans-serif',
    googleFontsHref: null,
  },
  inter: {
    id: 'inter',
    label: 'Inter',
    tagline: 'Notion · Linear · Vercel · Stripe — neutru și modern.',
    // C7: servit din repo — public/fonts + fonts.css (vezi layout.tsx).
    fontStack: 'var(--font-inter), "Inter", system-ui, -apple-system, sans-serif',
    googleFontsHref: null,
  },
  'ibm-plex': {
    id: 'ibm-plex',
    label: 'IBM Plex Sans',
    tagline: 'IBM Cloud — distinctiv, profesional, cu personalitate.',
    // C7: servit din repo — public/fonts + fonts.css.
    fontStack: 'var(--font-ibm-plex), "IBM Plex Sans", system-ui, -apple-system, sans-serif',
    googleFontsHref: null,
  },
  manrope: {
    id: 'manrope',
    label: 'Manrope',
    tagline: 'Geometric modern — rotunjimi blânde, ușor de citit.',
    // C7: servit din repo — public/fonts + fonts.css.
    fontStack: 'var(--font-manrope), "Manrope", system-ui, -apple-system, sans-serif',
    googleFontsHref: null,
  },
  roboto: {
    id: 'roboto',
    label: 'Roboto',
    tagline: 'Default-ul SoftFactura — servit din aplicație, zero CLS.',
    // Servit din repo — public/fonts + fonts.css (var --font-roboto).
    fontStack: 'var(--font-roboto), "Roboto", system-ui, -apple-system, sans-serif',
    // null → bootstrap script-ul nu injectează <link>, fontul e deja în bundle.
    googleFontsHref: null,
  },
  geist: {
    id: 'geist',
    label: 'Geist',
    tagline: 'Vercel · v0.dev · Linear — minimalism geometric modern, dev-tool vibe.',
    // C7: servit din repo — public/fonts + fonts.css.
    fontStack: 'var(--font-geist), "Geist", system-ui, -apple-system, sans-serif',
    googleFontsHref: null,
  },
  'mona-sans': {
    id: 'mona-sans',
    label: 'Mona Sans',
    tagline: 'GitHub · Copilot — variable axes (weight/width/slant), industrial elegant.',
    // C7: servit din repo — public/fonts + fonts.css.
    fontStack: 'var(--font-mona-sans), "Mona Sans", system-ui, -apple-system, sans-serif',
    googleFontsHref: null,
  },
  satoshi: {
    id: 'satoshi',
    label: 'Satoshi',
    tagline: 'Fontshare · adoptat de mii de startup-uri — geometric trendy 2024+.',
    fontStack: '"Satoshi", system-ui, -apple-system, sans-serif',
    googleFontsHref: 'https://api.fontshare.com/v2/css?f[]=satoshi@400,500,600,700&display=swap',
  },
  'dm-sans': {
    id: 'dm-sans',
    label: 'DM Sans',
    tagline: 'Framer · Webflow — low-contrast geometric, prietenos dar profesional.',
    // C7: servit din repo — public/fonts + fonts.css.
    fontStack: 'var(--font-dm-sans), "DM Sans", system-ui, -apple-system, sans-serif',
    googleFontsHref: null,
  },
  switzer: {
    id: 'switzer',
    label: 'Switzer',
    tagline: 'X-height 79% — cel mai lizibil sans la dimensiuni mici (10-13px).',
    fontStack: '"Switzer", system-ui, -apple-system, sans-serif',
    googleFontsHref: 'https://api.fontshare.com/v2/css?f[]=switzer@400,500,600,700&display=swap',
  },
  'general-sans': {
    id: 'general-sans',
    label: 'General Sans',
    tagline: 'Compact mobile-first — bun pentru tabele dense și sidebar labels.',
    fontStack: '"General Sans", system-ui, -apple-system, sans-serif',
    googleFontsHref: 'https://api.fontshare.com/v2/css?f[]=general-sans@400,500,600,700&display=swap',
  },
};

export const DEFAULT_FONT_FAMILY: FontFamilyId = 'roboto';

export interface FontSizePreset {
  id: FontSizeId;
  label: string;
  /** Aplicat pe `<html>` ca `font-size`. Schimbă valoarea de bază pentru `1rem`. */
  pct: string;
  /** Doar pentru afișaj UI pe cardul de selecție (text demonstrativ). */
  preview: string;
}

export const FONT_SIZE_PRESETS: Record<FontSizeId, FontSizePreset> = {
  sm: { id: 'sm', label: 'Mic',         pct: '92%',  preview: '13px' },
  md: { id: 'md', label: 'Mediu',       pct: '100%', preview: '14px' },
  lg: { id: 'lg', label: 'Mare',        pct: '108%', preview: '15px' },
  xl: { id: 'xl', label: 'Foarte mare', pct: '116%', preview: '16px' },
};

export const DEFAULT_FONT_SIZE: FontSizeId = 'md';

/** ID-ul folosit pentru `<link>` Google Fonts dinamic (cleanup la schimbare). */
export const FONT_LINK_ID = 'sf-font-dynamic';
